import React, { useRef, useState } from 'react';
import { Button, Form, InputGroup } from 'react-bootstrap';
import PropTypes from 'prop-types';

import {
  requestCommentInsert,
  requestCommentUpdate,
  requestCommentDelete
} from 'api/board/rawMaterial';
import { utc2tz } from 'function/convert';
import { onInputChange } from 'function/event';
import { getLoginInform } from 'function/login';
import { getText } from 'function/language';
import { getErrorMessage } from 'function/message';
import { isValidRequired } from 'function/valid';
import { useConfirmModalState } from 'global/confirmModalState';
import useObjReducer from 'hooks/useObjReducer';
import { RESPONSE_CODE } from 'types/const';
import { RETURN_YN } from 'types/type';
import format from 'utils/string/format';

/**
 * 협업 게시판 > 신규 원료 코드 신청 - 댓글
 */
const Comment = ({ datas, seq, requestInform }) => {
  /* variable */
  const { adminYn, userId, userNm } = getLoginInform();

  /* recoil */
  const { setConfirmModal } = useConfirmModalState();

  /* reference */
  const textareaEl = useRef(null);

  /* state */
  // 댓글 등록 요청 데이터
  const [insertData, setInsertData] = useObjReducer({
    rwmtrNewSeq: seq,
    comment: ''
  });
  // 댓글 수정 요청 데이터
  const [updateData, setUpdateData] = useObjReducer({ comment: '' });
  // 설정 비활성화 여부
  const [isDisableSetting, setIsDisableSetting] = useState(false);
  // 선택한 번호
  const [selectSeq, setSelectSeq] = useState(0);

  /* event */
  // 삭제 클릭 시
  const onDeleteClick = seq => {
    setConfirmModal({
      isOpen: true,
      title: 'delete',
      message: 'deleteMessage',
      isCancelBtn: true,
      btnNm: 'delete',
      async onConfirm() {
        try {
          const { code, message } = await requestCommentDelete(seq);

          if (code !== RESPONSE_CODE.Success) {
            throw {
              code,
              error: message
            };
          }

          // 신규 원료 코드 신청 정보 조회 요청
          await requestInform();
        } catch (error) {
          setConfirmModal({
            isOpen: true,
            message: getErrorMessage(error?.code, error?.error)
          });
        }
      }
    });
  };
  // 설정
  const onSubmit = () => {
    const isUpdate = selectSeq > 0;

    setConfirmModal({
      isOpen: true,
      title: isUpdate ? 'update' : 'insert',
      message: isUpdate ? 'updateMessage' : 'insertMessage',
      isCancelBtn: true,
      async onConfirm() {
        setIsDisableSetting(true);

        try {
          if (selectSeq > 0) {
            // 필수 항목 입력 유효성 확인
            if (!isValidRequired(updateData)) {
              throw {
                code: RESPONSE_CODE.BadRequestUpdate
              };
            }

            const { code, message } = await requestCommentUpdate(
              selectSeq,
              updateData
            );

            if (code !== RESPONSE_CODE.Success) {
              throw {
                code,
                error: message
              };
            }

            // 선택한 번호 초기화
            setSelectSeq(0);
          } else {
            // 필수 항목 입력 유효성 확인
            if (!isValidRequired(insertData)) {
              throw {
                code: RESPONSE_CODE.BadRequestInsert
              };
            }

            const { code, message } = await requestCommentInsert(insertData);

            if (code !== RESPONSE_CODE.Success) {
              throw {
                code,
                error: message
              };
            }

            // 댓글 등록 요청 데이터 초기화
            setInsertData({ comment: '' });
          }

          // 신규 원료 코드 신청 정보 조회 요청
          await requestInform();
        } catch (error) {
          setConfirmModal({
            isOpen: true,
            message: getErrorMessage(error?.code, error?.error)
          });
        } finally {
          setIsDisableSetting(false);
        }
      }
    });
  };
  // 수정 클릭 시
  const onUpdateClick = (seq = 0, comment = '') => {
    setSelectSeq(seq);
    setUpdateData({ comment });

    textareaEl.current?.focus?.();
  };

  return (
    <>
      <h5 className="my-3 fw-normal">{getText('comment')}</h5>
      <Form.Group className="mb-3">
        <Form.Label>
          {format(getText('commentUserNm'), {
            userNm,
            userId
          })}
        </Form.Label>
        <InputGroup>
          <Form.Control
            as="textarea"
            value={insertData.comment}
            autoFocus
            name="comment"
            placeholder={getText('commentPlaceholder')}
            onChange={event => onInputChange(event, setInsertData)}
          />
          <Button disabled={isDisableSetting} onClick={onSubmit}>
            {getText('ok')}
          </Button>
        </InputGroup>
      </Form.Group>
      <div className="comments fs--1">
        {datas.map(({ seq, regUsrNm, comment, regDt, userFlag }) => (
          <div key={`datas${seq}`} className="mt-3">
            <p className="mb-1 bg-200 rounded-3 p-2">
              <strong className="me-2">
                {format(getText('commentRegUsrNm'), { regUsrNm })}
              </strong>
              <span>{comment}</span>
            </p>
            <div className="px-2">
              {(adminYn === RETURN_YN.Y || userFlag === RETURN_YN.Y) && (
                <>
                  <a href="#!" onClick={() => onUpdateClick(seq, comment)}>
                    {getText('update')}
                  </a>{' '}
                  •{' '}
                  <a href="#!" onClick={() => onDeleteClick(seq)}>
                    {getText('delete')}
                  </a>{' '}
                  •{' '}
                </>
              )}
              {utc2tz(regDt)}
            </div>
            {seq === selectSeq && (
              <InputGroup>
                <Form.Control
                  ref={textareaEl}
                  as="textarea"
                  value={updateData.comment}
                  autoFocus
                  name="comment"
                  placeholder={getText('commentPlaceholder')}
                  onChange={event => onInputChange(event, setUpdateData)}
                />
                <Button disabled={isDisableSetting} onClick={onSubmit}>
                  {getText('ok')}
                </Button>
              </InputGroup>
            )}
          </div>
        ))}
      </div>
    </>
  );
};

Comment.propTypes = {
  // 목록
  datas: PropTypes.arrayOf(PropTypes.object),

  // 신규 원료 코드 신청 번호
  seq: PropTypes.number.isRequired,

  // 신규 원료 코드 신청 정보 조회 요청 함수
  requestInform: PropTypes.func.isRequired
};
Comment.defaultProps = { datas: [] };

export default Comment;
